import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Frown, Loader2, Meh, Smile } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from './ui/toaster';
import { useAuthStore } from '@/auth/store';
import { useSubmitClinicianUiFeedback } from '@/hooks/queries';
import { extractApiErrorMessage } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import type { ClinicianUiFeedbackSentiment } from '@/api/telemetry';

/**
 * Global "Share your feedback" dialog (opened from the header). A 3-point
 * sentiment + optional comment, recorded via the telemetry event endpoint as
 * `ClinicianUiFeedbackSubmitted`. The current route is captured automatically
 * so the team knows which screen the feedback is about.
 */
type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const SENTIMENTS: Array<{
  value: ClinicianUiFeedbackSentiment;
  label: string;
  icon: typeof Smile;
  activeClass: string;
}> = [
  { value: 'negative', label: 'Needs work', icon: Frown, activeClass: 'border-rose-300 bg-rose-50 text-rose-700' },
  { value: 'neutral', label: 'Okay', icon: Meh, activeClass: 'border-amber-300 bg-amber-50 text-amber-700' },
  { value: 'positive', label: 'Great', icon: Smile, activeClass: 'border-emerald-300 bg-emerald-50 text-emerald-700' },
];

const MAX_COMMENT = 1000;

export const FeedbackDialog = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const clinicianId = useAuthStore((s) => s.clinician?.id ?? null);
  const submit = useSubmitClinicianUiFeedback();

  const [sentiment, setSentiment] = useState<ClinicianUiFeedbackSentiment | null>(null);
  const [comment, setComment] = useState('');

  const reset = () => {
    setSentiment(null);
    setComment('');
  };

  const canSubmit =
    (sentiment !== null || comment.trim().length > 0) && clinicianId != null && !submit.isPending;

  const onSubmit = async () => {
    if (!canSubmit || clinicianId == null) return;
    try {
      await submit.mutateAsync({
        clinicianId,
        sentiment,
        comments: comment,
        page: location.pathname,
      });
      toast({ title: t('Thanks for your feedback!') });
      reset();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: t("Couldn't send your feedback"),
        description: extractApiErrorMessage(err) ?? undefined,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('Share your feedback')}</DialogTitle>
          <DialogDescription>
            {t('How is the new experience working for you? Your feedback goes straight to the team.')}
          </DialogDescription>
        </DialogHeader>

        {/* Sentiment */}
        <div className="flex justify-center gap-2">
          {SENTIMENTS.map((s) => {
            const Icon = s.icon;
            const active = sentiment === s.value;
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => setSentiment(active ? null : s.value)}
                aria-pressed={active}
                className={cn(
                  'flex flex-1 flex-col items-center gap-1 rounded-md border px-3 py-2 text-xs transition-colors',
                  active ? s.activeClass : 'border-input text-muted-foreground hover:bg-muted',
                )}
              >
                <Icon className="h-6 w-6" />
                {t(s.label)}
              </button>
            );
          })}
        </div>

        {/* Comment */}
        <div className="space-y-1">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT))}
            placeholder={t('Tell us more (optional)…')}
            rows={4}
            className="w-full resize-none rounded-md border border-input bg-background px-2.5 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <p className="text-right text-[0.625rem] text-muted-foreground">
            {comment.length}/{MAX_COMMENT}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submit.isPending}>
            {t('Cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit}>
            {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('Send feedback')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
